import { fetchBaseQuery } from '@reduxjs/toolkit/query';

import {
  DictionariesParametersType,
  OptionGroupsType,
  SpecificationsGroupsType,
  SpecificationsValuesType,
} from '@/features/dictionaries/types';
import { createApi } from '@/features/utils';
import {
  OptionGroupItemType,
  SpecificationGroupItemType,
  SpecificationsValueItemType,
} from '@/interfaces/cars.interfaces';
import { DataTypeEnum } from '@/interfaces/settings.interfaces';
import { IGrpcService } from '@/services/GrpcService';
import injectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';

/**
 * API справочников
 */
export const dictionariesApi = createApi({
  reducerPath: 'dictionaries',
  baseQuery: fetchBaseQuery(),
  endpoints: builder => ({
    specifications: builder.query<SpecificationsValuesType, DictionariesParametersType>({
      queryFn: async (parameters, api) => {
        const state = api.getState() as any;

        const { formatter } = state.config;

        const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);

        const { itemsClient } = grpcService;

        const { items } = await itemsClient.findPublished({
          ...parameters,
        });

        const specificationsItems = items.map(
          item => formatter?.(DataTypeEnum.Specification, item) ?? item,
        ) as SpecificationsValueItemType[];

        return { data: { items: specificationsItems } };
      },
    }),
    specificationGroups: builder.query<
      SpecificationsGroupsType,
      DictionariesParametersType
    >({
      queryFn: async (parameters, api) => {
        const state = api.getState() as any;

        const { formatter } = state.config;

        const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);

        const { itemsClient } = grpcService;

        const { items } = await itemsClient.findPublished({
          ...parameters,
        });

        const specificationGroups = items.map(
          item => formatter?.(DataTypeEnum.SpecificationGroup, item) ?? item,
        ) as SpecificationGroupItemType[];

        return { data: { items: specificationGroups } };
      },
    }),
    optionGroups: builder.query<OptionGroupsType, DictionariesParametersType>({
      queryFn: async (parameters, api) => {
        const state = api.getState() as any;

        const { formatter } = state.config;

        const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);

        const { itemsClient } = grpcService;

        const { items } = await itemsClient.findPublished({
          ...parameters,
        });

        const optionGroups = items.map(
          item => formatter?.(DataTypeEnum.OptionGroup, item) ?? item,
        ) as OptionGroupItemType[];

        return { data: { items: optionGroups } };
      },
    }),
  }),
});

const dictionariesReducer = dictionariesApi.reducer;

export default dictionariesReducer;
